import { useEffect, useState, useRef, useLayoutEffect } from "react";
import api, { resolveUrl } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

export default function Mentors() {
    const [mentors, setMentors] = useState([]);
    useEffect(() => { api.get("/mentors").then(r => setMentors(r.data)); }, []);

    return (
        <div className="min-h-screen relative grain">
            <Header />
            <main className="max-w-[1400px] mx-auto px-6 md:px-10 pt-14 pb-24">
                <div className="overline text-[#5C5C5C]">Mentores</div>
                <h1 className="font-display font-light text-5xl md:text-6xl mt-2 tracking-tight">
                    Vozes do <span className="font-editorial italic text-holo">Instituto</span>
                </h1>

                <div className="grid md:grid-cols-2 gap-10 mt-14">
                    {mentors.map((m) => <MentorCard key={m.id} mentor={m} />)}
                </div>
            </main>
            <Footer />
        </div>
    );
}

function MentorCard({ mentor: m }) {
    const [expanded, setExpanded] = useState(false);
    const [needsToggle, setNeedsToggle] = useState(false);
    const bioRef = useRef(null);

    // Detect if the bio actually overflows the 4-line clamp so we hide the button when not needed.
    useLayoutEffect(() => {
        const el = bioRef.current;
        if (!el) return;
        // Temporarily measure unclamped scrollHeight vs. clamped clientHeight
        const overflowing = el.scrollHeight > el.clientHeight + 1;
        setNeedsToggle(overflowing);
    }, [m.bio]);

    return (
        <article
            data-testid={`mentor-${m.id}`}
            className="bg-white rounded-3xl border border-black/5 overflow-hidden grid grid-cols-1 md:grid-cols-[200px_1fr] items-start"
        >
            {/* Fixed-size portrait photo — same standard size for every mentor */}
            <div className="w-full md:w-[200px] aspect-[4/5] bg-[#F8F8F5]">
                {m.photo ? (
                    <img
                        src={resolveUrl(m.photo)}
                        alt={m.name}
                        className="w-full h-full object-cover object-top"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center font-display text-4xl text-[#5C5C5C]">
                        {m.name?.[0] || "?"}
                    </div>
                )}
            </div>

            <div className="p-7">
                <div className="overline text-[#5C5C5C]">Mentor</div>
                <h2 className="font-display text-2xl mt-2 tracking-tight">{m.name}</h2>
                <div className="font-editorial italic text-[#5C5C5C] mt-1">{m.title}</div>

                {/* Bio with 4-line clamp + Leia mais toggle */}
                <p
                    ref={bioRef}
                    data-testid={`mentor-bio-${m.id}`}
                    className={`font-body text-sm text-[#5C5C5C] mt-4 ${expanded ? "" : "line-clamp-4"}`}
                >
                    {m.bio}
                </p>

                {needsToggle && (
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        data-testid={`mentor-toggle-${m.id}`}
                        className="mt-2 font-display text-xs tracking-wider uppercase text-[#1A1A1A] hover:text-black underline underline-offset-4 transition-colors"
                    >
                        {expanded ? "Mostrar menos" : "Leia mais"}
                    </button>
                )}

                <div className="flex flex-wrap gap-2 mt-5">
                    {(m.specialties || []).map((s) => (
                        <span
                            key={s}
                            className="text-xs font-display px-3 py-1.5 rounded-full bg-[#F8F8F5] border border-black/5"
                        >
                            {s}
                        </span>
                    ))}
                </div>

                {m.instagram && (
                    <div className="font-body text-sm text-[#5C5C5C] mt-5">{m.instagram}</div>
                )}
            </div>
        </article>
    );
}
